import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid2";

import { Plan } from "./plan";
import { clientConfig } from "../../awsClientConfig";
import { GetProtectionPlans } from "../../queries/GetProtectionPlans";
import FreeClientFormContext from "../free-client-enrolment/free-client-form-context";
import { ArrowBack } from "@mui/icons-material";
import Footer from "../footer";

interface Props {
  onPlanSelect: (plan: any) => void;
  plans: any[];
  recommandedPlan?: any;
  unavailableProtectionPlan?: any;
}

const PLANS = {
  FREE:"Free",
  BASIC: "Standard",
  STANDARD: "Premium",
  PRO: "Professional",
};

export function PlansTable({
  onPlanSelect,
  plans,
  recommandedPlan,
  unavailableProtectionPlan,
}: Props): React.JSX.Element {
  const getFeatures = (planInfo: any) => {
    switch (planInfo.Name) {
      case PLANS.BASIC:
        return [
          "Lifetime Audit Defense*",
          "Monthly Tax Report",
          "IRS Balance Tracking",
          "IRS Notice Analysis",
          "Tax ID Fraud Remediation",
          "Discounted Tax Resolution Services",
          "Tax ID Theft Alerts",
          "Live Phone Support",
        ];
      case PLANS.STANDARD:
        return [
          "Lifetime Audit Defense*",
          "Monthly Tax Report",
          "IRS Balance Tracking",
          "IRS Notice Analysis",
          "Tax ID Fraud Remediation",
          "Discounted Tax Resolution Services",
          "Tax ID Theft Alerts",
          "Live Phone Support",
          "Tax Returns Filed by Licensed Pros†",
          "$10,000 Tax Return Accuracy Guarantee‡",
        ];
      case PLANS.PRO:
        return [
          "Lifetime Audit Defense*",
          "Monthly Tax Report",
          "IRS Balance Tracking",
          "IRS Notice Analysis",
          "Tax ID Fraud Remediation",
          "Discounted Tax Resolution Services",
          "Tax ID Theft Alerts",
          "Live Phone Support",
          "Tax Returns Filed by Licensed Pros†",
          "$10,000 Tax Return Accuracy Guarantee‡",
          "Discounted Business Tax Returns",
        ];
        case PLANS.FREE:
        return [
          "Monthly Tax Report",
          "IRS Balance Tracking",
          "IRS Notice Analysis",
        ];
      default:
        return [];
    }
  };

  const getButtonClassName = (planInfo: any) => {
    switch (planInfo.Name) {
      case PLANS.BASIC:
      case PLANS.FREE:
        return "plan-outline-button";
      case PLANS.STANDARD:
        return "plan-fill-button";
      case PLANS.PRO:
        return "plan-professional-button";
      default:
        return "";
    }
  };

  return (
    <>
    <div className="page-container">
      <Box
        sx={{
          alignItems: "flex-center",
          pr: { xs: 2, md: 6, lg: 4 },
          mt: 1,
        }}
      >
        <Typography variant="h4" component={"h4"}>
          Select your plan
        </Typography>
      </Box>
      {recommandedPlan && (
        <Box
          sx={{
            alignItems: "flex-center",
            pr: { xs: 2, md: 6, lg: 4 },
            mt: 1,
          }}
        >
          <Typography variant="body1" component={"span"}>
            {"Based on your current tax situation we recommend our "}
            <Typography
              variant="body1"
              component={"span"}
              sx={{ fontWeight: "bold", textDecoration: "underline" }}
            >
              {recommandedPlan.Name}
            </Typography>
            {" Plan"}
          </Typography>
        </Box>
      )}
      <Grid container spacing={3} sx={{ marginTop: "0.5rem" }}>
        {plans &&
          plans.map((planInfo: any) => {
            let description = planInfo.Description;
            if (planInfo.Name === PLANS.FREE) {
              description = "Our Free Plan gives you access to our Monthly Task Risk Report. Select this option if you would like a trial to see for yourself."?.replace(
                "Free Plan",
                "<b>Free Plan</b>"
              );
            } else if (planInfo.Name === PLANS.PRO) {
              description = planInfo.Description?.replace(
                "Pro Plan",
                "<b>Pro Plan</b>"
              );
            } else {
              description = planInfo.Description?.replace(
                `${planInfo.Name} Plan`,
                `<b>${planInfo.Name} Plan</b>`
              );
            }
      
            return (
              <Grid size={{md:3,xs:12,sm:12}} key={planInfo.Id}>
                <Plan
                  action={
                    <Button
                      disabled={
                        unavailableProtectionPlan &&
                        unavailableProtectionPlan.Name === planInfo.Name
                      }
                      className={getButtonClassName(planInfo)}
                      onClick={() => onPlanSelect(planInfo)}
                    >{`Upgrade to ${planInfo.Name}`}</Button>
                  }
                  currency="USD"
                  description={description}
                  features={getFeatures(planInfo)}
                  id={planInfo.Name}
                  name={planInfo.Name}
                  price={planInfo.MonthlyPrice}
                />
              </Grid>
            );
          })}
      </Grid>
      <Box
        sx={{
          alignItems: "flex-start",
          pl: { xs: 2, md: 6, lg: 4 },
          pr: { xs: 2, md: 6, lg: 4 },
          mt: 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: "#667085", fontSize: "0.75rem", mt: 2 }}
        >
          * Lifetime dependent on being active customer of Optima Tax Shields.
          Please review Protection Agreement for details and limitations.
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "#667085", fontSize: "0.75rem" }}
        >
          † Limited to one personal tax return per year per agreement. Standard
          Plan is eligible for a wage earner return; Professional Plan is
          eligible for an independent contractor return.
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "#667085", fontSize: "0.75rem" }}
        >
          ‡ Applies to tax returns prepared by Optima Tax Relief.
        </Typography>
      </Box>
      </div>
      <Footer/>
    </>
  );
}
